import { Container, IDestroyOptions, Point, Text, TextStyle, utils } from "pixi.js";
import { Event } from "../../../../../shared/event-system/Event";

export class AdaptiveTextInput extends Container {
    public readonly onTextChanged: Event<string>;
    public readonly onStateChanged: Event<InputState>;

    private readonly inputText: Text;
    private readonly input: HTMLInputElement;
    private readonly inputSize: Point;
    private readonly paddings: Paddings;
    private readonly stateStyles: Map<InputState, TextStyle>;
    private readonly scaler: IScaler;
    private readonly defaultTextStyleValue: TextStyle;

    private container: Container;
    private background: Container;
    private state: InputState;

    private precalculatedScale: number;

    public get defaultTextStyle(): TextStyle {
        return this.defaultTextStyleValue;
    }

    public get inputTextView(): Text {
        return this.inputText;
    }

    public get view(): Container {
        return this.container;
    }

    public get viewBackground(): Container {
        return this.viewBackground;
    }

    public get value(): string {
        return this.input.value;
    }

    public set value(value: string) {
        this.input.value = value;
        this.inputText.visible = this.input.value.length === 0 && this.state === InputState.NONE;
    }

    public constructor(options: TextInputOptions) {
        super();
        this.container = new Container();
        this.onTextChanged = new Event();
        this.onStateChanged = new Event();
        this.stateStyles = new Map(options.stateStyles);
        this.scaler = options.scaler;
        this.precalculatedScale = this.scaler.browserHeight / this.scaler.referenceHeight;
        this.defaultTextStyleValue = options.textStyle;
        const defaultPaddings: Paddings = {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0
        };
        this.paddings = options.inputPaddings == null ? defaultPaddings : {
            ...defaultPaddings,
            ...options.inputPaddings
        };
        this.inputSize = options.inputSize ?? new Point(options.background.width - this.paddings.left - this.paddings.right, options.background.height - this.paddings.top - this.paddings.bottom);
        this.inputText = new Text(options.defaultInput ?? options.placeholderText ?? "", options.textStyle);
        this.input = document.createElement("input");
        this.input.style.position = "absolute"
        this.input.style.border = "none";
        this.input.style.outline = "none";
        this.input.style.backgroundColor = "transparent";
        this.input.style.margin = `0`;
        this.input.style.paddingBlock = "unset";
        this.input.style.paddingInline = "unset";
        this.input.style.letterSpacing = "1px";// Seems all text in pixi have this space

        this.input.setAttribute("inputmode", "text");
        if (options.readonly) {
            this.input.setAttribute("readonly", "readonly");
        }

        this.background = options.background;
        if (this.background != null) {
            this.container.addChild(this.background);
        }

        this.addChild(
            this.container,
            this.inputText
        );

        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onInput = this.onInput.bind(this);

        this.input.addEventListener("focus", this.onFocus);
        this.input.addEventListener("blur", this.onBlur);
        this.input.addEventListener("input", this.onInput);
        document.body.appendChild(this.input);
        this.scaler.onResize.add(this.onResize, this);

        this.layout();
        if (options.hidden) {
            this.hideHtmlInput();
        }

        this.on("removed", this.onRemoved, this);
        this.on("added", this.onAdded, this);
    }

    public hideHtmlInput(): void {
        this.input.style.display = "none";
        this.updateInputStyle();
    }

    public showHtmlInput(): void {
        this.input.style.display = "block";
        this.updateInputStyle();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.input.removeEventListener('focus', this.onFocus);
        this.input.removeEventListener('blur', this.onBlur);
        this.input.removeEventListener('input', this.onInput);
        this.input.remove();
        this.scaler.onResize.remove(this.onResize, this);
        this.off("removed", this.onRemoved, this);
        this.off("added", this.onAdded, this);
        super.destroy(options);
    }

    private switchState(state: InputState): void {
        if (this.state === state) {
            return;
        }
        this.state = state;
        this.inputText.visible = this.input.value.length === 0 && this.state === InputState.NONE;
        if (!this.stateStyles.has(state)) {
            this.inputText.style = this.defaultTextStyleValue;
            this.layout();
        }
        else {
            const style = this.stateStyles.get(state);
            this.inputText.style = style;
            this.layout();
        }
        this.onStateChanged.emit(state);
    }

    private layout(): void {
        this.inputText.position.set(
            this.container.position.x + this.paddings.left,
            this.container.position.y + this.paddings.top
        );
        this.updateInputStyle();
    }

    public updateInputStyle(): void {
        const scale = utils.isMobile.apple.device ? this.precalculatedScale : this.scaler.browserHeight / this.scaler.referenceHeight;
        const globalPosition = this.getGlobalPosition();
        const topPadding = Math.floor(this.inputText.y * scale);
        const bottomPadding = Math.floor((this.inputSize.y - this.inputText.height) * scale);
        this.input.style.fontSize = `${Math.floor(this.inputText.height * scale)}px`;
        this.input.style.fontFamily = this.inputText.style.fontFamily as string;
        this.input.style.fontWeight = this.inputText.style.fontWeight;
        this.input.style.color = this.inputText.style.fill.toString();
        this.input.style.left = `${Math.floor(globalPosition.x)}px`;
        this.input.style.top = `${Math.floor(globalPosition.y)}px`;
        this.input.style.width = `${Math.floor(this.inputSize.x * scale)}px`;
        this.input.style.height = `${Math.floor((this.inputSize.y * scale) - bottomPadding)}px`;
        this.input.style.paddingLeft = `${Math.floor(this.inputText.x * scale)}px`;
        this.input.style.paddingTop = `${topPadding}px`;
        this.input.style.paddingBottom = `${bottomPadding}px`;
        this.input.style.paddingRight = `${this.paddings.right}px`;
    }

    private onFocus(): void {
        //editing
        this.switchState(InputState.EDITING);
    }

    private onBlur(): void {
        //stop editing
        this.switchState(InputState.NONE);
    }

    private onInput(): void {
        // user input
        this.switchState(InputState.EDITING);
        this.onTextChanged.emit(this.input.value);
    }

    private onAdded(): void {
        this.hideHtmlInput();
    }

    private onRemoved(): void {
        this.showHtmlInput();
    }

    private onResize(_scales: { width: number, height: number }): void {
        this.layout();
    }
}

type Paddings = {
    readonly left?: number;
    readonly right?: number;
    readonly top?: number;
    readonly bottom?: number;
}

export enum InputState {
    NONE = 0,
    EDITING = 1,
    READONLY = 2
}

export interface IScaler {
    get onResize(): Event<{ height: number, width: number }>;
    get browserWidth(): number;
    get browserHeight(): number;
    get referenceWidth(): number;
    get referenceHeight(): number;
}

export interface TextInputOptions {
    textStyle: TextStyle;
    background: Container;
    scaler: IScaler;
    placeholderText?: string;
    defaultInput?: string;
    readonly?: boolean;
    inputSize?: Point;
    inputPaddings?: Paddings;
    stateStyles?: Map<InputState, TextStyle>;
    hidden?: boolean;
}